/**
 * RBP2 风格「图标 + 文案 + 面板」的可滚动 Tabs（打印机等页内子 Tab 使用）。
 * 与 `tab/scroll-tabs.component.tsx`（另一套 props）区分，勿混用。
 */
import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Tabs, Tab, Typography } from '@mui/material';

const useStyles = makeStyles(() => ({
    root: {
        flexGrow: 1,
        width: '100%',
    },
    tab: {
        display: 'flex',
        alignItems: 'center',
    },
    tabIcon: {
        display: 'flex',
        alignItems: 'center',
        marginRight: 8,
    },
    tabPanel: {
        minHeight: 300,
    },
}));

export type ScrollTabType = {
    /** 列表项 id */
    id: string;
    /** 标签前图标 */
    icon?: JSX.Element;
    /** 标签文案 */
    label: string;
    /** 面板内容 */
    component: JSX.Element;
};

/**
 * 可滚动 Tabs：带自定义标签布局与子面板切换。
 */
export default function ScrollTabs(props: {
    /** Tab 定义列表 */
    tabs: ScrollTabType[];
    /** 初始选中下标 */
    defaultValue?: number;
    /** 切换 Tab 时回调 */
    tabsClickEvent?: (val: number) => void;
    /** 根节点 className（用于页内高权重样式，如打印机设置桌台区） */
    className?: string;
    /** 与 Tabs 同一行右侧区域（如全选/取消全选），与标签栏对齐 */
    tabBarEnd?: React.ReactNode;
}) {
    const { defaultValue, tabsClickEvent, tabs, className: rootClassName, tabBarEnd } = props;
    const classes = useStyles();
    const [value, setValue] = React.useState(defaultValue ?? 0);
    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        tabsClickEvent?.(newValue);
    };

    return (
        <div className={`${classes.root}${rootClassName ? ` ${rootClassName}` : ''}`}>
            <Box className='scroll-tabs__bar-row' display='flex' alignItems='center' gap={1} width='100%'>
                <Box className='scroll-tabs__tabs-wrap' sx={{ flex: '1 1 auto', minWidth: 0 }}>
                    <Tabs value={value} onChange={handleChange} aria-label="tabs" variant="scrollable" scrollButtons="auto">
                        {tabs.map((item, index) => (
                            <Tab
                                key={`${item.id}-${index}`}
                                disableRipple
                                label={(
                                    <div className={classes.tab}>
                                        <div className={classes.tabIcon}>{item.icon}</div>
                                        <Typography>{item.label}</Typography>
                                    </div>
                                )}
                            />
                        ))}
                    </Tabs>
                </Box>
                {tabBarEnd ? (
                    <Box className='scroll-tabs__bar-end' sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', pl: 1, pr: 2 }}>
                        {tabBarEnd}
                    </Box>
                ) : null}
            </Box>
            {tabs.map((item, index) => (
                <TabPanel key={`${item.id}-${index}`} value={value} index={index}>
                    <div className={classes.tabPanel}>
                        {item.component}
                    </div>
                </TabPanel>
            ))}
        </div>
    );
}

function TabPanel(props: { children?: React.ReactNode; index: number; value: number }) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`scrollable-auto-tabpanel-${index}`}
            aria-labelledby={`scrollable-auto-tab-${index}`}
            {...other}
        >
            {value === index ? <Box>{children}</Box> : null}
        </div>
    );
}
